import React from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';

import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import Table from '../../../../Table';
import FormContainer from '../../../FormContainer';

import PropertyService from '../../../../../services/PropertyService';
import PropertVendorService from '../../../../../services/PropertyVendorService';
import usePropertyVendorForm from '../../../../../forms/loan/propertyVendor';
import { useUiContext } from '../../../../../context/UiContext';
import useUserRole from '../../../../../hooks/useUserRole';

const tabs = [
  {
    label: 'Property',
  },
  {
    label: 'Taxes',
  },
];

export default function Taxes({ loan }) {
  const propertyId = loan?._property_of_loan?.id || loan?._property?.id;

  const { isSuperAdmin } = useUserRole();

  const { showToast } = useUiContext();

  const { data: propertyVendors, refetch } = useQuery({
    queryKey: ['propertyVendors', propertyId],
    queryFn: () => PropertyService.getPropertyVendors(propertyId),
    enabled: !!propertyId,
  });

  const { mutate: createPropertyVendor } = useMutation({
    mutationFn: (formValues) =>
      PropertVendorService.createPropertyVendor({
        formValues,
        propertyId,
      }),
    onSuccess: () => {
      showToast({
        text: 'Success',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: deletePropertyVendor } = useMutation({
    mutationFn: (propertyVendorId) =>
      PropertVendorService.deletePropertyVendor(propertyVendorId),
    onSuccess: () => {
      showToast({
        text: 'Success',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: updatePropertyVendor } = useMutation({
    mutationFn: (formValues) =>
      PropertVendorService.updatePropertyVendor({
        formValues,
        propertyVendorId: formValues.id,
      }),
    onSuccess: () => {
      showToast({
        text: 'Success',
        type: 'success',
      });
      refetch();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const cols = [
    {
      title: 'Vendor',
      keys: ['_vendor.name'],
      link: (row) =>
        isSuperAdmin ? `/vendors/${row._vendor.id}?tab=Vendor` : '',
    },
    {
      title: 'Tax Identification Number',
      keys: ['tax_identification_number'],
      type: isSuperAdmin ? 'editableWithButtons' : 'text',
      inputType: 'text',
      onSubmit: (row, taxNumber) =>
        updatePropertyVendor({
          ...row,
          tax_identification_number: taxNumber,
        }),
    },
    {
      title: 'Tax Year',
      keys: ['tax_year'],
      type: isSuperAdmin ? 'editableWithButtons' : 'text',
      inputType: 'select',
      onSubmit: (row, taxYear) =>
        updatePropertyVendor({
          ...row,
          tax_year: taxYear,
        }),
      options: [
        {
          label: '2023',
          value: '2023',
        },
        {
          label: '2024',
          value: '2024',
        },
        {
          label: '2025',
          value: '2025',
        },
      ],
    },
    {
      title: 'None Due',
      keys: ['none_due'],
      type: isSuperAdmin ? 'checkbox' : 'text',
      isSelected: (row) => row.none_due,
      onChange: (row) =>
        updatePropertyVendor({
          ...row,
          none_due: !row.none_due,
        }),
      format: isSuperAdmin ? null : (value) => (value ? 'Yes' : 'No'),
    },
    {
      title: '',
      type: 'delete',
      onClick: (row) => deletePropertyVendor(row.id),
      isVisible: () => isSuperAdmin,
    },
  ];

  const form = usePropertyVendorForm(createPropertyVendor, loan?.id);

  return (
    <div>
      <div className="flex mb-4">
        <AddOrEditPageTabs tabs={tabs} />
      </div>

      <div
        className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 
        shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
      >
        <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6">
          Taxes
        </h2>
        <div className="mb-4">
          <Table cols={cols} rows={propertyVendors} title="Taxes" />
        </div>
        {isSuperAdmin && <FormContainer form={form} noBillComId />}
      </div>
    </div>
  );
}
